<template>
  <div class="geo-region-select" :class="{ 'is-off': disabled }">
    <div class="geo-box">
      <div class="geo-top">
        <select
          v-if="kind === 'geo'"
          class="geo-sel"
          :value="selectValue"
          :disabled="disabled"
          @change="onGeoSelect"
        >
          <option value="">{{ placeholder }}</option>
          <option v-for="opt in selectOptions" :key="opt.code" :value="opt.code">{{ opt.label }}</option>
        </select>
        <select
          v-else
          class="geo-sel"
          :value="''"
          :disabled="disabled"
          @change="onRegionAdd"
        >
          <option value="">{{ placeholder }}</option>
          <option v-for="opt in regionOptions" :key="opt.code" :value="opt.code">{{ opt.label }}</option>
        </select>
        <div v-if="kind === 'region' && showMode" class="geo-mode">
          <button type="button" class="inc" :class="{ on: !exclude }" :disabled="disabled" @click="setMode(false)">Include</button>
          <button type="button" class="exc" :class="{ on: exclude }" :disabled="disabled" @click="setMode(true)">Exclude</button>
        </div>
      </div>

      <ul v-if="picked.length" class="geo-picked">
        <li v-for="(code, index) in picked" :key="code + index" @click="remove(index)" :title="'Remove ' + labelFor(code)">
          <span class="geo-code">{{ code }}</span>
          <span class="geo-name">{{ catalogName(code) }}</span>
          <span class="geo-x">×</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import countries from '@/assets/countries.js'
import regions from '@/assets/us_states.js'
import { geoCode } from '../../v2/status'

const CA_PROVINCES = {
  AB: 'Alberta',
  BC: 'British Columbia',
  MB: 'Manitoba',
  NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador',
  NS: 'Nova Scotia',
  NT: 'Northwest Territories',
  NU: 'Nunavut',
  ON: 'Ontario',
  PE: 'Prince Edward Island',
  QC: 'Quebec',
  SK: 'Saskatchewan',
  YT: 'Yukon'
}

export default {
  name: 'GeoRegionSelect',
  props: {
    value: { type: String, default: '' },
    kind: { type: String, required: true, validator: v => v === 'geo' || v === 'region' },
    geo: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    showMode: { type: Boolean, default: true }
  },
  data() {
    return {
      codes: [],
      exclude: false
    }
  },
  computed: {
    catalog() {
      if (this.kind !== 'region') return countries
      return geoCode(this.geo) === 'CA' ? CA_PROVINCES : regions
    },
    placeholder() {
      if (this.kind !== 'geo') return 'Add region…'
      return this.codes.length > 1 ? 'Replace with…' : 'Select country…'
    },
    selectValue() {
      if (this.codes.length > 1) return ''
      return this.codes[0] || ''
    },
    picked() {
      if (this.kind === 'region') return this.codes
      return this.codes.length > 1 ? this.codes : []
    },
    selectOptions() {
      return this.buildOptions(false)
    },
    regionOptions() {
      return this.buildOptions(true)
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.syncFromValue(val)
      }
    }
  },
  methods: {
    buildOptions(excludeSelected) {
      const out = []
      Object.keys(this.catalog).forEach(code => {
        if (excludeSelected && this.codes.includes(code)) return
        out.push({ code, label: `${this.catalog[code]} (${code})` })
      })
      return out.sort((a, b) => a.label.localeCompare(b.label))
    },
    syncFromValue(val) {
      const raw = String(val || '').trim()
      const exclude = raw.charAt(0) === '!'
      const codes = raw.replace(/^!/, '').split(',').map(s => s.trim()).filter(Boolean)
      this.codes = codes
      // An empty value keeps a mode chosen before any code ("Exclude", then pick).
      if (codes.length) this.exclude = exclude
    },
    setMode(exclude) {
      if (this.disabled || this.exclude === exclude) return
      this.exclude = exclude
      this.emitValue()
    },
    catalogName(code) {
      return this.catalog[code] || code
    },
    labelFor(code) {
      return this.catalog[code] ? `${this.catalog[code]} (${code})` : code
    },
    onGeoSelect(e) {
      const code = e.target.value
      this.codes = code ? [code] : []
      this.emitValue()
    },
    onRegionAdd(e) {
      const code = e.target.value
      e.target.value = ''
      if (!code || this.codes.includes(code)) return
      this.codes.push(code)
      this.emitValue()
    },
    remove(index) {
      if (this.disabled) return
      this.codes.splice(index, 1)
      this.emitValue()
    },
    emitValue() {
      const body = this.codes.join(',')
      const exclude = this.kind === 'region' && this.exclude
      this.$emit('input', body && exclude ? `!${body}` : body)
    }
  }
}
</script>
